import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  error: Error | null;
}

/** Top-level boundary for uncaught render errors (spec §30: no silent failures). */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for diagnostics; a real deployment can wire this to
    // an error-reporting service. Never swallow silently.
    console.error('Uncaught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ minHeight: '100vh', p: 3, textAlign: 'center' }}
        >
          <Typography variant="h4">Unexpected error</Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={520}>
            The interface hit an unexpected problem. Reloading usually fixes it. If it keeps
            happening, share the details below with support.
          </Typography>
          <Box
            component="pre"
            sx={{
              maxWidth: 640,
              overflow: 'auto',
              fontSize: 12,
              bgcolor: 'action.hover',
              p: 2,
              borderRadius: 1,
              textAlign: 'left',
            }}
          >
            {this.state.error.message}
          </Box>
          <Button variant="contained" onClick={this.handleReload}>
            Reload
          </Button>
        </Stack>
      );
    }
    return this.props.children;
  }
}
